import { Box, Button, PasswordInput } from "@mantine/core";
import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router";
import { ROUTES } from "../../routes";
import { PasswordResetLayout } from "../Layout/PasswordResetLayout";
import { PublicLayout } from "../Layout/PublicLayout";
import { checkAuth } from "../Login/utils/checkAuth";
import { useSetPass } from "./hooks/useSetPass";
import { PasswordRequirementsList } from "./PasswordRequirementsList";
import { resetPasswordConfirm } from "./utils/resetPasswordConfirm";

export interface CustomPasswordErrors {
  isTooShort: boolean;
  doesNotContainLowerAndUppercaseLetter: boolean;
  doesNotContainNumberOrSpecialCharacter: boolean;
  containsUserEmail: boolean;
  passwordsDoNotMatch: boolean;
}

function PasswordResetConfirm() {
  const [newPass, setNewPass] = useState<string>("");
  const [newPassConfirm, setNewPassConfirm] = useState<string>("");
  const [isReady, setIsReady] = useState<boolean>(false);

  const {
    handleSetPass,
    isSettingPass,
    isPasswordSet,
    errors,
    isChecked,
    validationErrors,
    isInvalidPass
  } = useSetPass({
    newPass,
    newPassConfirm,
    onPasswordSet: resetPasswordConfirm
  });

  const [searchParams] = useSearchParams();

  const redirectUrl = useMemo(
    () => searchParams.get("next") ?? ROUTES.productSelectPage,
    [searchParams]
  );

  useEffect(() => {
    const prepareRender = async () => {
      const hasAccess = await checkAuth(redirectUrl);

      setIsReady(hasAccess);
    };

    prepareRender();
  }, [redirectUrl]);

  if (!isReady) return null;

  return (
    <PublicLayout>
      <PasswordResetLayout
        description={
          isPasswordSet ? (
            <>
              Ihr Passwort wurde gesetzt. Sie können sich jetzt mit dem Passwort{" "}
              <Link to="/login">einloggen</Link>.
            </>
          ) : (
            "Bitte geben Sie Ihr neues Passwort doppelt ein."
          )
        }
        title={isPasswordSet ? "Passwort gesetzt" : "Passwort ändern"}
      >
        {!isPasswordSet && (
          <form className="PasswordResetForm" onSubmit={handleSetPass}>
            <Box mb="1rem">
              <PasswordInput
                error={isInvalidPass}
                label="Neues Passwort"
                name="newPass"
                required
                value={newPass}
                withAsterisk={!newPass}
                onChange={(event) => setNewPass(event.currentTarget.value)}
              />

              <PasswordRequirementsList
                isChecked={isChecked}
                validationErrors={validationErrors}
              />

              <PasswordInput
                error={
                  isChecked && validationErrors.passwordsDoNotMatch
                    ? "Die eingegebenen Passwörter stimmen nicht überein."
                    : false
                }
                label="Neues Passwort bestätigen"
                name="newPassConfirm"
                required
                value={newPassConfirm}
                withAsterisk={!newPassConfirm}
                onChange={(event) =>
                  setNewPassConfirm(event.currentTarget.value)
                }
              />

              {errors}

              <div className="SubmitBtnWrapper">
                <Button
                  className="PasswordSubmitBtn"
                  color="primary"
                  disabled={!newPass || !newPassConfirm}
                  loading={isSettingPass}
                  type="submit"
                >
                  Passwort ändern
                </Button>
              </div>
            </Box>
          </form>
        )}
      </PasswordResetLayout>
    </PublicLayout>
  );
}

export { PasswordResetConfirm };
